import * as React from "react";
import {
  TextField,
  Link,
  Box,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useLogin } from "./hooks/useLogin";
import { getHeaders } from "./utils/HeaderHelper";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useFlash, FlashProvider } from "./FlashContext";
import FlashMessage from "./shared/FlashMessage";

interface LoginPageContent {
  heading1: string;
  heading2: string;
  heading3: string;
  subheading1: string;
  subheading2: string;
  subheading3: string;
  image_url: string;
}

interface Props {
  csrfToken: string;
  logo_src: string;
  button_src: string;
  login_bg_img_src: string;
}

const LoginForm: React.FC<Props> = (props: any) => {
  const { email, password, setEmail, setPassword } = useLogin();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [wasInitiallyAutofilledEmail, setWasInitiallyAutofilledEmail] =
    React.useState(false);
  const [wasInitiallyAutofilledPassword, setWasInitiallyAutofilledPassword] =
    React.useState(false);

  const [passwordHasValue, setPasswordHasValue] = React.useState(false);
  const [emailHasValue, setEmailHasValue] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState(0);
  const [showPassword, setShowPassword] = React.useState(false);
  const [loginPageContent, setLoginPageContent] = React.useState<LoginPageContent | null>(null);
  const [contentLoading, setContentLoading] = React.useState(true);
  const { showToast } = useFlash();

  // Fetch login page content
  React.useEffect(() => {
    fetch('/login_page_content', {
      method: 'GET',
    })
      .then(response => response.json())
      .then(data => {
        if (data.success && data.resource) {
          setLoginPageContent(data.resource);
        }
        setContentLoading(false);
      })
      .catch(error => {
        showToast({ title: "Error", text: "Failed to load page content.", type: "error" });
        setContentLoading(false);
      });
  }, [showToast]);

  const hasValue = (value) => value !== "";

  const CHECK_INTERVALS = [200, 500];

  React.useLayoutEffect(() => {
    const checkAutofilled = (
      fieldId: string,
      setAutofilled: React.Dispatch<React.SetStateAction<boolean>>
    ) => {
      const autofilled = !!document
        .getElementById(fieldId)
        ?.matches("*:-webkit-autofill");
      setAutofilled(autofilled);
    };

    const timeouts: NodeJS.Timeout[] = [];

    CHECK_INTERVALS.forEach((interval) => {
      timeouts.push(
        setTimeout(() => {
          checkAutofilled("email-address", setWasInitiallyAutofilledEmail);
          checkAutofilled("password", setWasInitiallyAutofilledPassword);
        }, interval)
      );
    });

    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  const fieldNotFilledYet = !email && !wasInitiallyAutofilledEmail;
  const passwordFieldNotFilledYet =
    !password && !wasInitiallyAutofilledPassword;
  const disableSubmitButton =
    !email || fieldNotFilledYet || !password || passwordFieldNotFilledYet;

  React.useEffect(() => {
    fetch("/set_timezone", {
      method: "POST",
      headers: getHeaders(props.csrfToken),
      body: JSON.stringify({
        time_zone: timeZone,
      }),
    });
  }, [timeZone]);

  // Default content as fallback
  const defaultSteps = [
    {
      label: "Smarter care starts here.",
      description: `Starfield helps you care for your patients with clarity and ease. Build custom questionnaires, manage immunizations, assign actions, and much more all in one simple, powerful platform.`,
    },
    {
      label: "Create an ad group",
      description:
        "Starfield helps you care for your patients with clarity and ease. Build custom questionnaires, manage immunizations, assign actions, and much more all in one simple, powerful platform.",
    },
    {
      label: "Create an ad group",
      description: `Starfield helps you care for your patients with clarity and ease. Build custom questionnaires, manage immunizations, assign actions, and much more all in one simple, powerful platform.`,
    },
  ];

  // Create steps from login page content or use default
  const steps = React.useMemo(() => {
    if (loginPageContent) {
      return [
        {
          label: loginPageContent.heading1 || defaultSteps[0].label,
          description: loginPageContent.subheading1 || defaultSteps[0].description,
        },
        {
          label: loginPageContent.heading2 || defaultSteps[1].label,
          description: loginPageContent.subheading2 || defaultSteps[1].description,
        },
        {
          label: loginPageContent.heading3 || defaultSteps[2].label,
          description: loginPageContent.subheading3 || defaultSteps[2].description,
        },
      ];
    }
    return defaultSteps;
  }, [loginPageContent]);

  const maxSteps = steps.length;

  React.useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prevStep) => (prevStep + 1) % maxSteps);
    }, 5000);

    return () => clearInterval(interval);
  }, [maxSteps]);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  const makeAnimationStartHandler = (stateSetter) => (e) => {
    const autoFilled = !!e.target?.matches("*:-webkit-autofill");
    if (e.animationName === "mui-auto-fill") {
      stateSetter(autoFilled);
    }

    if (e.animationName === "mui-auto-fill-cancel") {
      stateSetter(autoFilled);
    }
  };

  React.useEffect(() => {
    // Wait for autofill to complete
    requestAnimationFrame(() => {
      const emailEl = document.getElementById(
        "email-address"
      ) as HTMLInputElement;
      const passwordEl = document.getElementById(
        "password"
      ) as HTMLInputElement;

      if (emailEl?.value) {
        setEmail(emailEl.value);
        setEmailHasValue(true);
      }

      if (passwordEl?.value) {
        setPassword(passwordEl.value);
        setPasswordHasValue(true);
      }
    });
  }, []);

  return (
    <Box
      sx={{
        backgroundImage: `linear-gradient(46.33deg, rgba(33, 33, 33, 0.839216) 0%, rgba(66, 66, 66, 0.239216) 178.98%), url(${loginPageContent?.image_url || props.login_bg_img_src})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "100vh",
        display: "flex",
      }}
    >
      <Box
        sx={{
          width: "53%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "70%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            rowGap: "20px",
          }}
        >
          {contentLoading ? (
            <CircularProgress />
          ) : (
            <>
              {/* Use custom logo if available, otherwise use default */}
              <img 
                src={props.login_logo_src} 
                alt="Project Starfield" 
              />
              <Typography
                sx={{
                  fontSize: "39.06px",
                  fontWeight: 700,
                  lineHeight: "46.87px",
                  color: "white",
                  fontFamily: "QuicksandMedium",
                }}
              >
                {steps[activeStep].label}
              </Typography>
              <Box
                sx={{
                  minHeight: "85px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    lineHeight: "28.16px",
                    color: "white",
                    fontFamily: "QuicksandLight",
                  }}
                >
                  {steps[activeStep].description}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                {steps.map((_, index) => (
                  <Box
                    key={index + 1}
                    sx={{
                      height: activeStep === index ? "5px" : "3px",
                      transition: "all 0.3s ease",
                      width: activeStep === index ? "48px" : "32px",
                      backgroundColor: activeStep === index ? "white" : "#9E9E9E",
                    }}
                  />
                ))}
              </Box>
            </>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          width: "47%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
        }}
      >
        <Box
          sx={{
            width: "53%",
            height: "75vh",
            backgroundColor: "#EAEAEA",
            borderTopLeftRadius: "24px",
            borderTopRightRadius: "24px",
            padding: "34px",
          }}
        >
          <Typography
            sx={{
              fontSize: "12.8px",
              fontWeight: 400,
              lineHeight: "22.53px",
              fontFamily: "QuicksandRegular",
            }}
          >
            WELCOME BACK
          </Typography>
          <Typography
            sx={{
              fontSize: "25px",
              fontWeight: 500,
              lineHeight: "44px",
              marginBottom: "20px",
              fontFamily: "QuicksandRegular",
            }}
          >
            Log into Starfield
          </Typography>
          <form
            id="loginform"
            action="/users/sign_in"
            method="post"
            style={{ display: "flex", flexDirection: "column", rowGap: "20px" }}
          >
            <input
              type="hidden"
              name="authenticity_token"
              value={props.csrfToken}
            />

            <TextField
              id="email-address"
              label="Email Address*"
              required
              fullWidth
              value={email}
              name="user[email]"
              onChange={(event) => {
                setEmail(event.target.value);
                setEmailHasValue(hasValue(event.target.value));
              }}
              autoComplete="off"
              slotProps={{
                input: {
                  disableUnderline: true,
                  onAnimationStart: makeAnimationStartHandler(setEmailHasValue),
                },
                inputLabel: {
                  required: false,
                  shrink: emailHasValue,
                },
              }}
              sx={textfieldStyle}
            />

            <TextField
              id="password"
              required
              fullWidth
              value={password}
              label="Password*"
              name="user[password]"
              onChange={(event) => {
                setPassword(event.target.value);
                setPasswordHasValue(hasValue(event.target.value));
              }}
              type={showPassword ? "text" : "password"}
              autoComplete="off"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={
                          showPassword
                            ? "hide the password"
                            : "display the password"
                        }
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        onMouseUp={handleMouseUpPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  disableUnderline: true,
                  onAnimationStart:
                    makeAnimationStartHandler(setPasswordHasValue),
                },
                inputLabel: {
                  required: false,
                  shrink: passwordHasValue,
                },
              }}
              sx={textfieldStyle}
            />

            <Link
              href="/users/password/new"
              onClick={() => {}}
              sx={{
                fontSize: "12.8px",
                fontWeight: 400,
                lineHeight: "22.53px",
                color: "#424242",
                textDecoration: "none",
              }}
            >
              Forgot Password?
            </Link>

            <Button
              type="submit"
              disabled={disableSubmitButton}
              fullWidth
              sx={loginButtonStyle}
            >
              Continue
            </Button>
          </form>
        </Box>
      </Box>
    </Box>
  );
};

const textfieldStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#BDBDBD",
      borderRadius: "8px",
    },
    "&:hover fieldset": {
      borderColor: "#f8890b",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#f8890b",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "14px !important",
    fontSize: "14px",
    WebkitBoxShadow: "0 0 0 1000px #EAEAEA inset",
    transition: "background-color 5000s ease-in-out 0s",
  },
  "& label": {
    color: "#BEBEBE",
  },
  "& label.Mui-focused": {
    color: "#424242",
  },
};

const loginButtonStyle = {
  backgroundColor: "#f8890b",
  color: "#F7F7F7",
  fontSize: "12.8px",
  lineHeight: "22.53px",
  fontWeight: 700,
  height: "50px",
  borderRadius: "8px",
  "&.Mui-disabled": {
    backgroundColor: "#cccccc",
    color: "gray",
  },
};

const Login: React.FC<Props> = (props) => (
  <FlashProvider>
    <FlashMessage />
    <LoginForm {...props} />
  </FlashProvider>
);

export default Login;
